import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {
  getAppEnvironment,
  getSentryDsn,
  getSentryReplayOnErrorSampleRate,
} from '@/config/env-config';
import { getTenantSlugFromHostname } from '@/utils/path-utils';
import { PROTECTED_URLS } from './protected-urls';
import { scrubTelemetryUrl } from './scrub-url';

// One Sentry project per app, split only by environment — so local dev must
// not report, or its noise is indistinguishable from a deployed stage.
const REPORTING_ENVIRONMENTS = ['production', 'staging'];

const TRACES_SAMPLE_RATE = 1;

const REPLAY_SESSION_SAMPLE_RATE = 0;

interface ReplayUrlsEvent extends Sentry.Event {
  urls?: string[];
}

function isProtectedUrl(url: string) {
  return PROTECTED_URLS.some(pattern => pattern.test(url));
}

function scrubRequestUrl<TEvent extends Sentry.Event>(event: TEvent) {
  if (event.request?.url) {
    event.request.url = scrubTelemetryUrl(event.request.url);
  }

  return event;
}

// beforeSend/beforeBreadcrumb never see the recording, so span frames
// (fetch, xhr, navigation) carry raw URLs unless filtered here.
function filterReplayRecordingEvent(frameEvent: Sentry.ReplayFrameEvent) {
  const { data } = frameEvent;

  if (data.tag !== 'performanceSpan') {
    return frameEvent;
  }

  if (isProtectedUrl(data.payload.description)) {
    return null;
  }

  data.payload.description = scrubTelemetryUrl(data.payload.description);

  return frameEvent;
}

function scrubReplayEventUrls(event: ReplayUrlsEvent) {
  if (event.type === 'replay_event' && event.urls) {
    event.urls = event.urls.map(scrubTelemetryUrl);
  }

  return event;
}

function buildReplayIntegration() {
  return Sentry.replayIntegration({
    // Privacy invariants, not defaults: an SDK flip must never start recording.
    maskAllText: true,
    maskAllInputs: true,
    blockAllMedia: true,
    beforeAddRecordingEvent: filterReplayRecordingEvent,
  });
}

// Same-origin CloudFront proxy (SPRK-6017): /ingest/m/<project> is
// rewritten at the edge to the Sentry envelope endpoint, so ad blockers and
// CSP never see sentry.io. The DSN pathname is /<project_id>.
function sentryTunnelPath(dsn: string) {
  return `/ingest/m${new URL(dsn).pathname}`;
}

function initializeSentrySdk(dsn: string, environment: string) {
  const replayOnErrorSampleRate = getSentryReplayOnErrorSampleRate();
  const isReplayEnabled = replayOnErrorSampleRate > 0;

  Sentry.init({
    dsn,
    environment,
    // No release here: the Vite plugin injects the one the sourcemaps use.
    tunnel: sentryTunnelPath(dsn),

    // No enableLogs: AxiosError.toJSON() puts the request body in console args.
    integrations: [
      Sentry.reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        shouldCreateSpanForRequest: url => !isProtectedUrl(url),
      }),
      ...(isReplayEnabled ? [buildReplayIntegration()] : []),
    ],

    tracesSampleRate: TRACES_SAMPLE_RATE,
    replaysSessionSampleRate: REPLAY_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: replayOnErrorSampleRate,

    beforeSend: scrubRequestUrl,
    beforeSendTransaction: scrubRequestUrl,
    beforeBreadcrumb: breadcrumb => {
      if (typeof breadcrumb.data?.url === 'string') {
        const url = breadcrumb.data.url;

        if (isProtectedUrl(url)) {
          return null;
        }

        breadcrumb.data.url = scrubTelemetryUrl(url);
      }

      return breadcrumb;
    },
  });

  if (isReplayEnabled) {
    Sentry.addEventProcessor(scrubReplayEventUrls);
  }

  // Credential-entry surface: the tenant is the only identity we attach —
  // never Sentry.setUser, which would carry the username being typed.
  Sentry.setTag('tenant', getTenantSlugFromHostname());
}

export function initializeSentry() {
  const dsn = getSentryDsn();
  const environment = getAppEnvironment();

  if (!dsn || !REPORTING_ENVIRONMENTS.includes(environment)) {
    return;
  }

  // Runs during module evaluation (instrument.ts) — a malformed DSN must
  // never take the login page down with it
  try {
    initializeSentrySdk(dsn, environment);
  } catch (error) {
    console.error('[Sentry] Failed to initialize:', error);
  }
}
